import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  ArrowsRightLeftIcon,
  CheckIcon,
  PlayIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import {
  LeagueService,
  SeasonService,
  LeagueSeasonService,
  TeamService,
  ContestantService,
  RealtimeService,
} from '../../services/firebaseService';
import { DraftService } from '../../services/draftService';
import DraftRoomBoard from '../../components/draft/DraftRoomBoard';
import {
  DRAFT_STATUS,
  areAllRoomsComplete,
  getDraftRoomCountOptions,
} from '../../utils/draftUtils';
import { getTeamName } from '../../utils/teamUtils';

export default function ManageDraft() {
  const { leagueId, seasonId } = useParams();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();

  const [league, setLeague] = useState(null);
  const [globalSeason, setGlobalSeason] = useState(null);
  const [leagueSeason, setLeagueSeason] = useState(null);
  const [teams, setTeams] = useState([]);
  const [contestants, setContestants] = useState([]);
  const [claimersByTeamId, setClaimersByTeamId] = useState({});
  const [activeRoomId, setActiveRoomId] = useState(1);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [moveTeamId, setMoveTeamId] = useState('');
  const [moveToRoomId, setMoveToRoomId] = useState('');
  const [selectedRoomCount, setSelectedRoomCount] = useState(null);

  const isCommissioner = useMemo(() => {
    if (!userProfile || !leagueId) return false;
    return (
      userProfile.managedLeagues?.includes(leagueId) ||
      league?.managerId === currentUser?.uid
    );
  }, [userProfile, leagueId, league, currentUser]);

  const draftRooms = leagueSeason?.draftRooms || [];
  const draftStatus = leagueSeason?.draftStatus || DRAFT_STATUS.NOT_STARTED;
  const isSetup = draftStatus === DRAFT_STATUS.NOT_STARTED;
  const isLive =
    draftStatus === DRAFT_STATUS.IN_PROGRESS ||
    draftStatus === DRAFT_STATUS.COMPLETE ||
    draftStatus === DRAFT_STATUS.FINALIZED;
  const isFinalized = draftStatus === DRAFT_STATUS.FINALIZED;
  const allRoomsComplete = areAllRoomsComplete(draftRooms);
  const canFinalize =
    draftStatus === DRAFT_STATUS.COMPLETE && allRoomsComplete && !isFinalized;

  const contestantCount =
    globalSeason?.contestants?.length || contestants.length || 0;
  const roomCountOptions = useMemo(
    () => getDraftRoomCountOptions(teams.length, contestantCount || 1),
    [teams.length, contestantCount]
  );

  const activeRoom =
    draftRooms.find(r => r.id === activeRoomId) || draftRooms[0] || null;

  const teamsSortedByName = useMemo(
    () => [...teams].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [teams]
  );

  useEffect(() => {
    let unsubSeason = null;
    let unsubTeams = null;

    async function init() {
      try {
        setLoading(true);
        const [leagueData, seasonData, lsData, teamData] = await Promise.all([
          LeagueService.getLeague(leagueId),
          SeasonService.getSeason(seasonId),
          LeagueSeasonService.getLeagueSeason(leagueId, seasonId),
          TeamService.getTeams(leagueId, seasonId),
        ]);

        setLeague(leagueData);
        setGlobalSeason(seasonData);
        setLeagueSeason(lsData);
        setTeams(teamData);

        const contestantIds = seasonData?.contestants || [];
        let contestantList = [];
        if (contestantIds.length) {
          const all = await ContestantService.getContestantsBySeason(seasonId);
          const byId = Object.fromEntries(all.map(c => [c.id, c]));
          // Prefer season list order; fall back to by-season query
          contestantList = contestantIds
            .map(id => byId[id] || { id, name: id })
            .filter(Boolean);
          if (contestantList.length === 0) contestantList = all;
        } else {
          contestantList =
            await ContestantService.getContestantsBySeason(seasonId);
        }
        setContestants(contestantList);

        const status = await TeamService.getTeamsClaimingStatus(
          teamData.map(t => t.id),
          leagueId,
          seasonId
        );
        setClaimersByTeamId(DraftService.claimersMapFromStatus(status));

        if (lsData?.draftRooms?.[0]) {
          setActiveRoomId(lsData.draftRooms[0].id);
        }

        unsubSeason = RealtimeService.listenToLeagueSeason(
          leagueId,
          seasonId,
          data => {
            if (data) setLeagueSeason(data);
          }
        );
        unsubTeams = DraftService.listenToTeams(leagueId, seasonId, setTeams);
      } catch (err) {
        console.error(err);
        toast.error('Failed to load draft');
      } finally {
        setLoading(false);
      }
    }

    init();
    return () => {
      if (unsubSeason) unsubSeason();
      if (unsubTeams) unsubTeams();
    };
  }, [leagueId, seasonId]);

  useEffect(() => {
    if (!roomCountOptions.length) {
      setSelectedRoomCount(null);
      return;
    }
    const existingCount = draftRooms.length;
    const existingIsValid = roomCountOptions.some(
      o => o.roomCount === existingCount
    );
    const fallback = existingIsValid
      ? existingCount
      : roomCountOptions[0].roomCount;
    setSelectedRoomCount(prev => {
      if (prev != null && roomCountOptions.some(o => o.roomCount === prev)) {
        return prev;
      }
      return fallback;
    });
  }, [roomCountOptions, draftRooms.length]);

  const handleSetup = async () => {
    try {
      setBusy(true);
      const rooms = await DraftService.setupDraft(
        leagueId,
        seasonId,
        contestantCount,
        claimersByTeamId,
        selectedRoomCount
      );
      setActiveRoomId(rooms[0]?.id || 1);
      toast.success(
        `Created ${rooms.length} draft room${rooms.length === 1 ? '' : 's'}`
      );
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Failed to set up draft');
    } finally {
      setBusy(false);
    }
  };

  const handleReshuffle = async (roomId = null) => {
    try {
      setBusy(true);
      await DraftService.reshuffleOrders(
        leagueId,
        seasonId,
        draftRooms,
        roomId
      );
      toast.success('Draft order reshuffled');
    } catch (err) {
      toast.error(err.message || 'Failed to reshuffle');
    } finally {
      setBusy(false);
    }
  };

  const handleMoveTeam = async () => {
    if (!moveTeamId || !moveToRoomId) return;
    try {
      setBusy(true);
      await DraftService.moveTeam(
        leagueId,
        seasonId,
        draftRooms,
        moveTeamId,
        Number(moveToRoomId)
      );
      setMoveTeamId('');
      toast.success('Team moved');
    } catch (err) {
      toast.error(err.message || 'Failed to move team');
    } finally {
      setBusy(false);
    }
  };

  const handleMoveUp = async (roomId, teamId) => {
    const room = draftRooms.find(r => r.id === roomId);
    if (!room) return;
    const order = [...room.teamOrder];
    const idx = order.indexOf(teamId);
    if (idx <= 0) return;
    [order[idx - 1], order[idx]] = [order[idx], order[idx - 1]];
    try {
      setBusy(true);
      await DraftService.reorderRoom(
        leagueId,
        seasonId,
        draftRooms,
        roomId,
        order
      );
    } catch (err) {
      toast.error(err.message || 'Failed to reorder');
    } finally {
      setBusy(false);
    }
  };

  const handleMoveDown = async (roomId, teamId) => {
    const room = draftRooms.find(r => r.id === roomId);
    if (!room) return;
    const order = [...room.teamOrder];
    const idx = order.indexOf(teamId);
    if (idx < 0 || idx >= order.length - 1) return;
    [order[idx + 1], order[idx]] = [order[idx], order[idx + 1]];
    try {
      setBusy(true);
      await DraftService.reorderRoom(
        leagueId,
        seasonId,
        draftRooms,
        roomId,
        order
      );
    } catch (err) {
      toast.error(err.message || 'Failed to reorder');
    } finally {
      setBusy(false);
    }
  };

  const handleStart = async () => {
    if (
      !window.confirm(
        'Start the live draft? Room assignments and pick order will be locked.'
      )
    ) {
      return;
    }
    try {
      setBusy(true);
      await DraftService.startDraft(leagueId, seasonId, draftRooms);
      toast.success('Draft started');
    } catch (err) {
      toast.error(err.message || 'Failed to start draft');
    } finally {
      setBusy(false);
    }
  };

  const handlePick = async ({ roomId, teamId, contestantId }) => {
    try {
      setPicking(true);
      await DraftService.makePick({
        leagueId,
        seasonId,
        roomId,
        teamId,
        contestantId,
        userId: currentUser.uid,
        userProfile,
        isCommissioner: true,
      });
      toast.success('Pick submitted');
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Failed to make pick');
    } finally {
      setPicking(false);
    }
  };

  const handleUndo = async roomId => {
    if (!window.confirm('Undo the most recent pick in this room?')) return;
    try {
      setUndoing(true);
      await DraftService.undoLastPick({
        leagueId,
        seasonId,
        roomId,
        isCommissioner: true,
      });
      toast.success('Pick undone');
    } catch (err) {
      toast.error(err.message || 'Failed to undo');
    } finally {
      setUndoing(false);
    }
  };

  const handleFinalize = async () => {
    if (
      !window.confirm(
        'Finalize the draft? Picks will be locked and cannot be undone.'
      )
    ) {
      return;
    }
    try {
      setFinalizing(true);
      await DraftService.finalizeDraft({
        leagueId,
        seasonId,
        isCommissioner: true,
      });
      toast.success('Draft finalized');
    } catch (err) {
      toast.error(err.message || 'Failed to finalize draft');
    } finally {
      setFinalizing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-survivor-500" />
      </div>
    );
  }

  if (!league) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-gray-700 mb-4">
            This league does not exist or is no longer available.
          </p>
          <button
            type="button"
            onClick={() => navigate('/manage-leagues')}
            className="text-survivor-600 font-medium hover:underline min-h-[44px]"
          >
            Back to leagues
          </button>
        </div>
      </div>
    );
  }

  if (!isCommissioner) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <p className="text-gray-700">
          Only the league commissioner can manage this draft.
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <button
          type="button"
          onClick={() => navigate(`/manage-leagues/${leagueId}`)}
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4 min-h-[44px]"
        >
          <ArrowLeftIcon className="w-4 h-4 mr-2" />
          Back to league
        </button>

        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            Draft — {league?.name}
          </h1>
          <p className="text-gray-600 mt-1">
            {globalSeason?.name || seasonId} · {teams.length} teams ·{' '}
            {contestantCount} contestants
          </p>
        </div>

        {isSetup && (
          <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 mb-6 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Draft setup
                </h2>
                <p className="text-sm text-gray-600">
                  Create rooms, adjust order, keep families together, then
                  start.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={busy || !roomCountOptions.length}
                  onClick={handleSetup}
                  className="inline-flex items-center min-h-[44px] px-4 py-2 rounded-md text-white bg-survivor-600 hover:bg-survivor-700 text-sm font-medium disabled:opacity-50"
                >
                  {draftRooms.length
                    ? 'Regenerate rooms'
                    : 'Set up draft rooms'}
                </button>
                {draftRooms.length > 0 && (
                  <>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => handleReshuffle(null)}
                      className="inline-flex items-center min-h-[44px] px-4 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Reshuffle all orders
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={handleStart}
                      className="inline-flex items-center min-h-[44px] px-4 py-2 rounded-md text-white bg-green-600 hover:bg-green-700 text-sm font-medium disabled:opacity-50"
                    >
                      <PlayIcon className="w-4 h-4 mr-2" />
                      Start draft
                    </button>
                  </>
                )}
              </div>
            </div>

            {roomCountOptions.length > 0 && (
              <div className="max-w-md">
                <label
                  htmlFor="draft-room-count"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Draft rooms
                </label>
                <select
                  id="draft-room-count"
                  value={
                    selectedRoomCount ?? roomCountOptions[0]?.roomCount ?? ''
                  }
                  onChange={e => setSelectedRoomCount(Number(e.target.value))}
                  disabled={busy}
                  className="w-full min-h-[44px] rounded-md border-gray-300 text-sm"
                >
                  {roomCountOptions.map(option => (
                    <option key={option.roomCount} value={option.roomCount}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Each room drafts from the full contestant pool. Changing this
                  applies when you set up or regenerate rooms.
                </p>
              </div>
            )}

            {draftRooms.length > 0 && (
              <>
                <div className="flex flex-col sm:flex-row gap-2 sm:items-end border-t border-gray-100 pt-4">
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Move team
                    </label>
                    <select
                      value={moveTeamId}
                      onChange={e => setMoveTeamId(e.target.value)}
                      className="w-full min-h-[44px] rounded-md border-gray-300 text-sm"
                    >
                      <option value="">Select team…</option>
                      {teamsSortedByName.map(t => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="sm:w-40">
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      To room
                    </label>
                    <select
                      value={moveToRoomId}
                      onChange={e => setMoveToRoomId(e.target.value)}
                      className="w-full min-h-[44px] rounded-md border-gray-300 text-sm"
                    >
                      <option value="">Room…</option>
                      {draftRooms.map(r => (
                        <option key={r.id} value={r.id}>
                          Room {r.id}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    type="button"
                    disabled={busy || !moveTeamId || !moveToRoomId}
                    onClick={handleMoveTeam}
                    className="inline-flex items-center justify-center min-h-[44px] px-4 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium disabled:opacity-50"
                  >
                    <ArrowsRightLeftIcon className="w-4 h-4 mr-2" />
                    Move
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {draftRooms.map(room => (
                    <div
                      key={room.id}
                      className="border border-gray-200 rounded-lg p-4"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-semibold text-gray-900">
                          Room {room.id}{' '}
                          <span className="text-sm font-normal text-gray-500">
                            ({room.teamOrder.length} teams)
                          </span>
                        </h3>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => handleReshuffle(room.id)}
                          className="text-xs text-survivor-700 hover:underline"
                        >
                          Reshuffle
                        </button>
                      </div>
                      <ol className="space-y-1">
                        {room.teamOrder.map((teamId, index) => {
                          const claimers = claimersByTeamId[teamId] || [];
                          return (
                            <li
                              key={teamId}
                              className="flex items-center justify-between gap-2 text-sm bg-gray-50 rounded px-2 py-1.5"
                            >
                              <span className="min-w-0 truncate">
                                <span className="text-gray-500 mr-1">
                                  #{index + 1}
                                </span>
                                {getTeamName(teamId, teams)}
                                {claimers.length > 1 && (
                                  <span className="ml-1 text-xs text-amber-700">
                                    (shared claimers)
                                  </span>
                                )}
                              </span>
                              <span className="shrink-0 flex gap-1">
                                <button
                                  type="button"
                                  className="px-2 py-1 text-xs border rounded min-h-[32px]"
                                  onClick={() => handleMoveUp(room.id, teamId)}
                                  disabled={busy || index === 0}
                                >
                                  ↑
                                </button>
                                <button
                                  type="button"
                                  className="px-2 py-1 text-xs border rounded min-h-[32px]"
                                  onClick={() =>
                                    handleMoveDown(room.id, teamId)
                                  }
                                  disabled={
                                    busy || index === room.teamOrder.length - 1
                                  }
                                >
                                  ↓
                                </button>
                              </span>
                            </li>
                          );
                        })}
                      </ol>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {isLive && (
          <div className="space-y-4">
            {isFinalized && (
              <div className="rounded-md bg-green-50 border border-green-200 text-green-800 text-sm px-4 py-3">
                Draft finalized. Picks are locked and read-only for everyone.
              </div>
            )}
            {canFinalize && (
              <div className="rounded-md bg-blue-50 border border-blue-200 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <p className="text-sm text-blue-900">
                  All picks are in. Finalize the draft to lock results and
                  prevent further changes.
                </p>
                <button
                  type="button"
                  disabled={finalizing}
                  onClick={handleFinalize}
                  className="inline-flex items-center justify-center min-h-[44px] px-4 py-2 rounded-md text-white bg-blue-600 hover:bg-blue-700 text-sm font-medium disabled:opacity-50 shrink-0"
                >
                  <CheckIcon className="w-4 h-4 mr-2" />
                  {finalizing ? 'Finalizing…' : 'Finalize draft'}
                </button>
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {draftRooms.map(room => {
                const onClock = room.teamOrder?.[room.currentPickIndex];
                return (
                  <button
                    key={room.id}
                    type="button"
                    onClick={() => setActiveRoomId(room.id)}
                    className={`min-h-[44px] px-4 py-2 rounded-md text-sm font-medium border ${
                      activeRoom?.id === room.id
                        ? 'bg-survivor-600 text-white border-survivor-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    Room {room.id}
                    {room.status === 'complete'
                      ? ' · Done'
                      : onClock
                        ? ` · ${getTeamName(onClock, teams)}`
                        : ''}
                  </button>
                );
              })}
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
              <DraftRoomBoard
                room={activeRoom}
                teams={teams}
                contestants={contestants}
                myTeamIds={[]}
                isCommissioner
                readOnly={isFinalized}
                onPick={handlePick}
                onUndo={isFinalized ? undefined : handleUndo}
                picking={picking}
                undoing={undoing}
              />
            </div>
          </div>
        )}

        {!isSetup && !isLive && (
          <p className="text-gray-600">Unknown draft status.</p>
        )}
      </div>
    </div>
  );
}
